import React, { useState } from "react";
import { VisuallyHidden } from "react-aria";
import { ButtonContext } from "react-aria-components";
import { t } from "ttag";

import type { StepSlideBlock, StepByStepBlock as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../styles/themes";
import { Carousel } from "../Carousel/Carousel";
import { Clickable } from "../Clickables";
import type { DataRow } from "../GlassCard/GlassCard";
import { GlassCard } from "../GlassCard/GlassCard";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { SKSArrows } from "../SKSCarousel/Arrows";
import { SKSDots } from "../SKSCarousel/Dots";
import { MotionPauseContext } from "../SKSCarousel/MotionPauseContext";
import {
    isAutoplayVideoMedia,
    useVideoPacedAutoPlay,
} from "../SKSCarousel/useVideoPacedAutoPlay";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

interface AutoPlayControlProps {
    isPaused: boolean;
}

function AutoPlayControl({ isPaused }: Readonly<AutoPlayControlProps>) {
    return (
        <Clickable slot="autoplay" className={styles.pauseButton}>
            <VisuallyHidden>{isPaused ? t`Play` : t`Pause`}</VisuallyHidden>
            <Svg name={isPaused ? "play" : "pause"} aria-hidden="true" />
        </Clickable>
    );
}

interface VideoPauseControlProps {
    isPaused: boolean;
    onPress: () => void;
}

/**
 * Same look and placement as AutoPlayControl, for when auto-advance is off:
 * there's no rotation to control, but autoplay videos still need a pause
 * control somewhere (WCAG 2.2.2) and this block's design has no room for
 * per-video chrome, so this one speaks only for the videos.
 */
function VideoPauseControl({
    isPaused,
    onPress,
}: Readonly<VideoPauseControlProps>) {
    return (
        // The empty ButtonContext shields this button from the carousel's
        // slotted context, which throws for slotless buttons inside it
        <ButtonContext.Provider value={{}}>
            <Clickable onPress={onPress} className={styles.pauseButton}>
                <VisuallyHidden>
                    {isPaused ? t`Play Video` : t`Pause Video`}
                </VisuallyHidden>
                <Svg name={isPaused ? "play" : "pause"} aria-hidden="true" />
            </Clickable>
        </ButtonContext.Provider>
    );
}

interface StepProgressBarProps {
    slides: readonly StepSlideBlock[];
    activeIndex: number;
    autoPlayControl?: React.ReactNode;
}

function StepProgressBar({
    slides,
    activeIndex,
    autoPlayControl,
}: Readonly<StepProgressBarProps>) {
    return (
        <div className={styles.stepBar}>
            {slides.map((slide, index) => {
                const stepNumber = String(index + 1).padStart(2, "0");
                const isActive = index === activeIndex;
                return (
                    <div
                        key={index}
                        className={concatClassNames([
                            styles.step,
                            isActive ? styles.stepActive : undefined,
                        ])}
                    >
                        <div className={styles.stepLabelRow}>
                            <span className={styles.stepLabel}>
                                {stepNumber}. {slide.step_label}
                            </span>
                            {isActive && autoPlayControl}
                        </div>
                        <div className={styles.stepTrack}>
                            {isActive && (
                                <div className={styles.stepTrackFill} />
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

interface SlideProps {
    slide: StepSlideBlock;
    collapsible: boolean;
    videoProps?: React.ComponentProps<typeof MediaChooserBlock>["videoProps"];
}

function Slide({ slide, collapsible, videoProps }: Readonly<SlideProps>) {
    const dataRows: DataRow[] | undefined =
        slide.data_rows && slide.data_rows.length > 0
            ? slide.data_rows.flatMap((row) =>
                  row.label != null && row.description != null
                      ? [{ label: row.label, description: row.description }]
                      : [],
              )
            : undefined;

    const heading = slide.heading ?? "";
    const body = slide.body ?? "";

    return (
        <div className={styles.slide}>
            <div className={styles.mediaContainer}>
                {slide.media && (
                    <MediaChooserBlock
                        value={slide.media}
                        videoProps={videoProps}
                    />
                )}
            </div>
            <div className={styles.gradientOverlay} />
            <GlassCard
                className={styles.card}
                heading={heading}
                body={body}
                link={slide.link}
                dataEyebrow={slide.data_eyebrow}
                dataRows={dataRows}
                collapsible={collapsible}
            />
        </div>
    );
}

interface StepByStepBlockProps {
    value: Value;
}

export const StepByStepBlock = ({ value }: Readonly<StepByStepBlockProps>) => {
    const [isUserPaused, setIsUserPaused] = useState(false);

    const slides = value.slides ?? [];
    const hasMultipleSlides = slides.length > 1;
    const autoAdvance = value.auto_advance !== false;
    const shouldAutoPlay = hasMultipleSlides && autoAdvance;

    const slideMedia = slides.map((slide) => slide.media);
    const {
        carouselApi,
        carouselContainer,
        activeIndex,
        isAutoPlayRunning,
        videoPacesAutoPlay,
        onActiveChange,
        onAutoPlayRunningChange,
        videoPropsFor,
    } = useVideoPacedAutoPlay({ slideMedia, enabled: shouldAutoPlay });

    const activeSlideIsAutoplayVideo = isAutoplayVideoMedia(
        slideMedia[activeIndex],
    );

    if (slides.length === 0) return null;

    // The active step shows a play/pause control, but it has a different
    // meaning based on whether or not the carousel is set to auto-advance.
    //
    // Auto-advancing: one control (on every slide) pauses rotation and videos
    // together. The videos pause with *every* rotation-pause condition, not
    // just the button, because the video is the rotation: left playing while
    // rotation is paused, it would advance the carousel when it ends. The
    // button icon still reflects user intent only.
    //
    // Not auto-advancing: there's no rotation, but autoplay videos still need
    // a pause control, so video slides get one.
    let isMotionPaused: boolean | null = null;
    let pauseControl: React.ReactNode;
    if (shouldAutoPlay) {
        isMotionPaused = !isAutoPlayRunning;
        pauseControl = <AutoPlayControl isPaused={isUserPaused} />;
    } else if (slideMedia.some(isAutoplayVideoMedia)) {
        isMotionPaused = isUserPaused;
        if (activeSlideIsAutoplayVideo) {
            pauseControl = (
                <VideoPauseControl
                    isPaused={isUserPaused}
                    onPress={() => setIsUserPaused((paused) => !paused)}
                />
            );
        }
    }

    return (
        <Themed tag="section" theme="black" className={styles.root}>
            <MotionPauseContext.Provider value={isMotionPaused}>
                <Carousel
                    label={value.block_label ?? t`Step by Step Guide`}
                    containerProps={{ ref: carouselContainer }}
                    apiRef={carouselApi}
                    slideContainerProps={{
                        className: styles.slideContainer,
                    }}
                    controls={({ allVisible }) => (
                        <>
                            <SKSArrows
                                allVisible={allVisible}
                                className={styles.arrows}
                            />
                            <StepProgressBar
                                slides={slides}
                                activeIndex={activeIndex}
                                autoPlayControl={pauseControl}
                            />
                        </>
                    )}
                    dots={({ allVisible, slideCount }) => (
                        <SKSDots
                            allVisible={allVisible}
                            slideCount={slideCount}
                            className={styles.dots}
                        />
                    )}
                    loop={hasMultipleSlides}
                    autoPlay={shouldAutoPlay}
                    autoPlayExternallyPaced={videoPacesAutoPlay}
                    pauseAutoPlayOnHover={false}
                    onAutoPlayPausedChange={setIsUserPaused}
                    onAutoPlayRunningChange={onAutoPlayRunningChange}
                    onActiveChange={onActiveChange}
                >
                    {slides.map((slide: StepSlideBlock, index: number) => (
                        <Slide
                            key={index}
                            slide={slide}
                            collapsible
                            videoProps={videoPropsFor(index)}
                        />
                    ))}
                </Carousel>
            </MotionPauseContext.Provider>
        </Themed>
    );
};
